import { useQuery } from '@apollo/client';
import { Typography } from '@mui/material';
import { GridPaginationModel } from '@mui/x-data-grid-premium';
import { Filter, useSearchkit } from '@searchkit/client';
import { isEmpty, isEqual, remove } from 'lodash';
import React, { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import useDeepCompareEffect from 'use-deep-compare-effect';
import ShowrunnrAIButton from '../../person/detailsPage/personResume/ShowrunnrAIButton';
import { FacetsList } from '../../supply/searchkit/facets/FacetsList';
import FacetsPanel from '../../supply/searchkit/facets/FacetsPanel';
import { UserContext } from '../auth/UserContext';
import type { SecuredActionType } from '../components/ActionType';
import BreadCrumbs from '../components/BreadCrumbs';
import Confirm from '../components/Confirm';
import { SortModel, UNSET_SORT, usePersistedSearchkitVariables } from '../components/filtering/filters';
import PinnedSavedFilterTabs from '../components/PinnedSavedFilterTabs';
import SavedFilterControls from '../components/SavedFilterControls';
import { FilterType } from '../components/types';
import {
    FETCH_POLICY_CONFIG,
    FILTER_IDENTIFIERS,
    LIST_VIEW_CONSTANTS,
    ROUTE_CONSTANTS,
    SIDE_PANEL_IDS
} from '../constants/constants';
import type { EntityReportAdapter } from '../generateReport/adapters/EntityReportAdapter';
import { resolveEntityReportAdapter } from '../generateReport/adapters/entityReportAdapterRegistry';
import GenerateReportStepper from '../generateReport/GenerateReportStepper';
import { useFetchReport } from '../generateReport/useFetchReport';
import useDefaultFilters from '../hooks/useDefaultFilters';
import I18n from '../i18n/I18n';
import { Icons } from '../icons/Icons';
import { Conditional, H, V } from '../Layout';
import useHistory from '../utils/useHistory';
import { collectViewContextFilterValues } from './createDefaultsFromViewContext';
import { combineVariables, sortFiltersForQuery } from './DataGrid/dataGridUtils';
import ReportButton from './GenerateAndViewButtons';
import { useKnownFacets } from './hooks/useKnownFacets';
import { useListViewInitialFilter } from './hooks/useListViewInitialFilter';
import { ListViewAddActions } from './ListViewAddActions';
import ListViewConfigurations from './ListViewConfigurations';
import { useStartCreateSession } from './listViewCreateSession';
import { ListViewHeaderTitle } from './ListViewHeaderTitle';
import { ListViewInitialFilterAlert } from './ListViewInitialFilterAlert';
import { SavedFiltersProvider } from './savedFilters';
import { useListViewToolbarQuickAddButtonPropsSecurity } from './useListViewToolbarQuickAddButtonPropsSecurity';
import { View } from './ViewTypeSelector';

const EMPTY_ARRAY = [];
const EMPTY_OBJECT: Readonly<Record<string, unknown>> = Object.freeze({});

export default function ListView({
    id,
    entity,
    feature,
    uri = undefined,
    query,
    EntityListRenderer = undefined,
    EntityTableRenderer = undefined,
    EntityGalleryRenderer = undefined,
    EntityScheduleRenderer = undefined,
    EntityRankViewRenderer = undefined,
    EntityCardListRenderer = EMPTY_ARRAY,
    EntityEditor = undefined,
    EntityDetails = undefined,
    ImportView = undefined,
    preFilters = [],
    immutableFilters = undefined,
    showFacets = true,
    enableImport = false,
    enableImportIcon = false,
    enableSearch = false,
    addActions = [] as SecuredActionType[],
    editDisabled = false,
    defaultSortModel = { field: 'name', sort: 'asc' } as SortModel,
    promoteFacets = undefined,
    showBreadCrumbs = true,
    containerSx = undefined,
    saveFiltersDisabled = false,
    excludeFiltersFromPersistence = undefined,
    notifyOnNetworkStatusChange = false,
    enableSearchLoader = false,
    facetsVisibility = { filters: true, searchPanel: true, promotedFacets: true, quickFilter: true },
    quickFilters = [],
    searchPlaceholder = undefined,
    initialSearchQuery = undefined,
    searchQuery = undefined,
    defaultView = undefined,
    createUri = false,
    filtersWithSameIdentifier = [],
    immutableFiltersWithSameIdentifier = [],
    defaultFilters = [],
    customVariables = undefined,
    enableShowrunnrAIAgentPanel = false,
    quickAddButtonProps = undefined,
    /** When true, toolbar write CTAs (import, etc.) are resolved via centralized security in ListView. */
    shouldUseCentralizedSecurityFramework = false,
    skipQuery = false,
    sameIdentifierKeyToRemove = undefined,
    shouldApplyDefaultSort = true,
    showConfigurationButtonForView = [] as string[],
    facetLabelProps = {
        labelMap: undefined,
        showLabelInitials: false
    },
    updateQuickFilters = undefined,
    additionalOptimisticData = undefined,
    preferenceEntityKey = undefined,
    mapEntityToCsvRows = undefined,
    exportFileName = undefined,
    headerTitle = undefined,
    showReportButton = true,
    defaultFilterAutoApply = true,
    customPageSize = undefined,
    prependViewContent = undefined,
    initialFilter = undefined,
    onClearInitialFilter = undefined,
    /** When set, renders this instead of the active view (e.g. table). Use to avoid re-creating content on parent re-renders. */
    overrideContent = undefined,
    /**
     * Extra props forwarded to the active view renderer. Use this only for renderer-specific dependencies that
     * ListView does not own. Standard ListView renderer props are applied after viewProps and take precedence.
     * Treat this object as read-only.
     */
    viewProps = EMPTY_OBJECT,
    ...props
}) {
    const { isAuthenticated, activeOrganizationAccount, application, userProfile } = useContext(UserContext);
    const { routeParams, changeRoute, searchParams, changeSearch } = useHistory();
    const { entityId } = routeParams;

    const resolvedExcludeFiltersFromPersistence = useMemo<string[] | undefined>(() => {
        const filterIdentifiersToExclude = new Set<string>(excludeFiltersFromPersistence || []);

        if (initialFilter?.identifier) {
            filterIdentifiersToExclude.add(initialFilter.identifier);
        }

        return filterIdentifiersToExclude.size > 0 ? Array.from(filterIdentifiersToExclude) : undefined;
    }, [excludeFiltersFromPersistence, initialFilter?.identifier]);

    const { variables, sortModel, setSortModel, pageNumber, setPageNumber, pageSize, setPageSize } =
        usePersistedSearchkitVariables(id, {
            saveFiltersDisabled,
            excludeFiltersFromPersistence: resolvedExcludeFiltersFromPersistence,
            defaultSortField: shouldApplyDefaultSort ? `${defaultSortModel.field}_${defaultSortModel.sort}` : '',
            ...(customPageSize ? { defaultPageSize: customPageSize } : {})
        });
    const { fetchReportData, reportData, reportLoading } = useFetchReport(query);

    const api = useSearchkit();
    const startCreateSession = useStartCreateSession();
    const prevAIFiltersRef = useRef<Filter[]>([]);
    const prevFilters = useRef(variables.filters);
    const isSavedFilterChangeRef = useRef(false);
    const latestQueriedVariablesRef = useRef<ReturnType<typeof combineVariables> | undefined>(undefined);
    const [aiFilters, setAiFilters] = useState<FilterType[]>([]);

    const securedQuickAddButtonProps = useListViewToolbarQuickAddButtonPropsSecurity(quickAddButtonProps);

    const onChangeAIQuery = (data: { nlquery?: { filters?: FilterType[] } }) => {
        setAiFilters(data?.nlquery?.filters || []);
    };

    const editMode = !!entityId;

    const views: View[] = useMemo(() => {
        const views = [];
        if (EntityTableRenderer) {
            views.push({
                icon: Icons.ListViewList,
                label: LIST_VIEW_CONSTANTS.tableView,
                Component: EntityTableRenderer,
                showFacets: true
            });
        }
        if (EntityListRenderer) {
            views.push({
                icon: Icons.CardListIcon,
                label: LIST_VIEW_CONSTANTS.listView,
                Component: EntityListRenderer,
                showFacets: true
            });
        }
        if (EntityGalleryRenderer) {
            views.push({
                icon: Icons.ListViewGallery,
                label: LIST_VIEW_CONSTANTS.galleryView,
                Component: EntityGalleryRenderer,
                showFacets: true
            });
        }
        if (EntityScheduleRenderer) {
            views.push({
                icon: Icons.Schedule,
                label: LIST_VIEW_CONSTANTS.scheduleView,
                Component: EntityScheduleRenderer,
                showFacets: false
            });
        }
        if (EntityCardListRenderer) {
            EntityCardListRenderer.forEach((renderer) => {
                views.push({
                    icon: renderer.icon,
                    label: renderer.label,
                    Component: renderer.component,
                    showFacets: true
                });
            });
        }
        if (EntityRankViewRenderer) {
            views.push({
                icon: Icons.RankOrder,
                label: LIST_VIEW_CONSTANTS.rankOrderView,
                Component: EntityRankViewRenderer,
                showFacets: true
            });
        }
        return views;
    }, [
        EntityTableRenderer,
        EntityListRenderer,
        EntityGalleryRenderer,
        EntityScheduleRenderer,
        EntityCardListRenderer,
        EntityRankViewRenderer
    ]);

    const currentView = searchParams.get(`${id}_${ROUTE_CONSTANTS.view}`);

    const [listLoading, setListLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [editorOpen, setEditorOpen] = useState(false);
    const [importPanelOpen, setImportPanelOpen] = useState(false);
    const [detailsVisible, setDetailsVisible] = useState(editMode);
    const [deleteConfirmationOpen, setDeleteConfirmationOpen] = useState(false);
    const [activeEntity, setActiveEntity] = useState('');
    const [currentData, setCurrentData] = useState(null);
    const [openShowrunnrAIAgentPanel, setOpenShowrunnrAIAgentPanel] = useState<boolean>(false);
    const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
        page: pageNumber,
        pageSize
    });
    const [selectedView, setSelectedView] = useState(0);
    const [reportVisible, setReportVisible] = useState(false);
    const [isConfigurationClicked, setIsConfigurationClicked] = useState<boolean>(false);
    const [reportControlledSidePanelId, setReportControlledSidePanelId] = useState<string | undefined>(undefined);
    const entityReportAdapter = useMemo(
        () =>
            resolveEntityReportAdapter({
                adapter: props.entityReportAdapter as EntityReportAdapter | undefined,
                adapterKey: props.entityReportAdapterKey as string | undefined
            }),
        [props.entityReportAdapter, props.entityReportAdapterKey]
    );
    const hasReportCapability = Boolean(props.generateReportButton || entityReportAdapter);
    const resolvedGenerateReportButton = useMemo(
        () =>
            props.generateReportButton ||
            (hasReportCapability
                ? {
                      icon: Icons.Report
                  }
                : undefined),
        [hasReportCapability, props.generateReportButton]
    );
    const resolvedReportButtonLabelToken =
        props.reportButtonLabelToken || entityReportAdapter?.reportButtonLabelToken || 'report.stepper.create';
    const normalizedPreferenceEntityKey = preferenceEntityKey?.trim().toLowerCase();
    const reportContextTerm = normalizedPreferenceEntityKey
        ? `${normalizedPreferenceEntityKey}StatusReport`
        : undefined;
    const resolvedControlledSidePanelId = props.controlledSidePanelId || reportControlledSidePanelId;
    const handleControlledSidePanelClose = () => {
        props.onControlledSidePanelClose?.();
        setReportControlledSidePanelId(undefined);
    };
    const handleOpenReportViewPanel = () => {
        if (props.onOpenSidePanel) {
            props.onOpenSidePanel();
            return;
        }

        setReportControlledSidePanelId(SIDE_PANEL_IDS.statusReports);
    };
    const activeView = views[selectedView];
    const showConfigurationButton = showConfigurationButtonForView.includes(activeView?.label);
    const hasFacets = showFacets && activeView && activeView.showFacets;
    const displayAddActions = Boolean(
        activeView && !activeView.disableAddActions && (addActions.length > 0 || EntityEditor || createUri)
    );

    const showActionsAndFacets =
        hasFacets ||
        enableImport ||
        securedQuickAddButtonProps?.enableQuickAddButtons ||
        views.length > 1 ||
        displayAddActions;

    const derivedSortBy = useMemo(() => {
        if (!isEmpty(sortModel) && sortModel.field !== UNSET_SORT && sortModel.sort !== UNSET_SORT) {
            return `${sortModel.field}_${sortModel.sort.toUpperCase()}`;
        }

        if (shouldApplyDefaultSort && defaultSortModel?.field && defaultSortModel?.sort) {
            return `${defaultSortModel.field}_${defaultSortModel.sort.toUpperCase()}`;
        }

        return '';
    }, [sortModel, shouldApplyDefaultSort, defaultSortModel?.field, defaultSortModel?.sort]);

    const applyImmutableFiltersWithSameIdentifier = useCallback(
        (baseFilters: Filter[] = []) => {
            const nextFilters = [...baseFilters];

            if (!isEmpty(immutableFiltersWithSameIdentifier)) {
                if (sameIdentifierKeyToRemove) {
                    remove(nextFilters, { identifier: sameIdentifierKeyToRemove });
                }

                immutableFiltersWithSameIdentifier.forEach((filter) => {
                    nextFilters.push(filter);
                });
            }

            return nextFilters;
        },
        [immutableFiltersWithSameIdentifier, sameIdentifierKeyToRemove]
    );

    const baseMergedVariables = combineVariables(variables, {
        ...(customVariables ?? {}),
        sortBy: customVariables?.sortBy ?? derivedSortBy
    });

    const resolvedFiltersWithInitialFilter = useMemo(() => {
        const nextFilters = [...(baseMergedVariables.filters || [])];

        if (initialFilter?.identifier) {
            const hasInitialFilter = nextFilters.some((filter) => isEqual(filter, initialFilter));

            if (!hasInitialFilter) {
                nextFilters.push(initialFilter);
            }
        }

        return applyImmutableFiltersWithSameIdentifier(nextFilters);
    }, [applyImmutableFiltersWithSameIdentifier, baseMergedVariables.filters, initialFilter]);

    const mergedVariables = {
        ...baseMergedVariables,
        page: { from: pageNumber * pageSize, size: pageSize },
        // Memoized filters can predate doRefetch's in-place entity insertion. Scope automatic queries too.
        filters: sortFiltersForQuery([
            ...resolvedFiltersWithInitialFilter.filter((filter) => filter.identifier !== FILTER_IDENTIFIERS.entity),
            { identifier: FILTER_IDENTIFIERS.entity, value: entity }
        ])
    };
    const skipUseQuery = resolvedFiltersWithInitialFilter.length === 0 || skipQuery;

    useDeepCompareEffect(() => {
        // Track latest variables dispatched through useQuery so auto-refetch can avoid duplicate calls.
        if (!skipUseQuery) {
            latestQueriedVariablesRef.current = mergedVariables;
        }
    }, [mergedVariables, skipUseQuery]);

    const { data, loading, refetch, error } = useQuery(query, {
        variables: mergedVariables,
        skip: skipUseQuery,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        },
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        notifyOnNetworkStatusChange,
        onCompleted: () => {
            setCurrentData(data);
        }
    });

    const sortModelMemoized = useMemo(() => (sortModel ? [sortModel] : []), [sortModel?.field, sortModel?.sort]);

    useEffect(() => {
        if (data?.results?.hits?.items && !isEqual(data.results.hits.items, currentData)) {
            setCurrentData(data);
        }
    }, [data?.results?.hits?.items]);

    useEffect(() => {
        if (initialSearchQuery) handleSearchQueryChange(initialSearchQuery);
    }, []);

    useEffect(() => {
        if (searchQuery) handleSearchQueryChange(searchQuery);
    }, [searchQuery]);

    const handleSearchQueryChange = (query: string) => {
        variables.query = query;
        setPageNumber(0);
        doRefetch();
    };

    useEffect(() => {
        setDetailsVisible(!!entityId);
    }, [entityId]);

    useEffect(() => {
        if (!isEmpty(immutableFilters)) {
            immutableFilters.forEach((preFilter: Filter) => {
                remove(variables.filters, { identifier: preFilter.identifier });
                variables.filters.push(preFilter);
            });
        }
    }, [variables, immutableFilters]);

    useEffect(() => {
        if (!isEmpty(filtersWithSameIdentifier)) {
            const removeFilterByIdentifierAndValue = (identifier, value) => {
                remove(variables.filters, (filter) => filter.identifier === identifier && filter.value === value);
            };
            filtersWithSameIdentifier.forEach((filter) => {
                removeFilterByIdentifierAndValue(filter.identifier, filter.value);
                variables.filters.push(filter);
            });
        }
    }, [variables, filtersWithSameIdentifier]);

    const doRefetch = useCallback(
        async (skipIfAlreadyQueried = false) => {
            if (isAuthenticated && !skipQuery) {
                variables.sortBy = customVariables?.sortBy ?? derivedSortBy;

                variables.page = { from: pageNumber * pageSize, size: pageSize };

                // Remove and add entity.
                remove(variables.filters, { identifier: FILTER_IDENTIFIERS.entity });
                variables.filters.push({ identifier: FILTER_IDENTIFIERS.entity, value: entity });

                // Merge variables and customVariables before refetching
                const filtersWithInitialFilter = [...(variables.filters || [])];

                if (initialFilter?.identifier) {
                    const hasInitialFilter = filtersWithInitialFilter.some((filter) => isEqual(filter, initialFilter));

                    if (!hasInitialFilter) {
                        filtersWithInitialFilter.push(initialFilter);
                    }
                }

                const mergedVariables = combineVariables(
                    {
                        ...variables,
                        filters: sortFiltersForQuery(applyImmutableFiltersWithSameIdentifier(filtersWithInitialFilter))
                    },
                    customVariables
                );

                if (skipIfAlreadyQueried && isEqual(latestQueriedVariablesRef.current, mergedVariables)) {
                    setIsSearching(false);
                    return;
                }

                setListLoading(true);
                latestQueriedVariablesRef.current = mergedVariables;
                try {
                    const res = await refetch(mergedVariables);
                    if (res?.data) {
                        setCurrentData(res?.data);
                    }
                } catch (err) {
                    console.error(err);
                } finally {
                    setListLoading(false);
                    setIsSearching(false);
                }
            }
        },
        [
            applyImmutableFiltersWithSameIdentifier,
            customVariables,
            derivedSortBy,
            entity,
            initialFilter,
            isAuthenticated,
            pageNumber,
            pageSize,
            refetch,
            skipQuery,
            variables
        ]
    );

    const resetPagination = useCallback(() => {
        setPageNumber(0);
        setPaginationModel({
            page: 0,
            pageSize
        });
    }, [pageSize, setPageNumber]);

    const { activeInitialFilter, handleClearInitialFilter } = useListViewInitialFilter({
        initialFilter,
        immutableFilters,
        onClearInitialFilter,
        variables,
        resetPagination,
        doRefetch
    });

    useEffect(() => {
        if (
            !aiFilters ||
            aiFilters.length === 0 ||
            (prevAIFiltersRef.current && isEqual(prevAIFiltersRef.current, aiFilters))
        ) {
            return;
        }

        const currentFilters = api.getFilters();
        if (!isEmpty(variables.query)) {
            variables.query = '';
        }

        // Create a Set of allowed filters
        const allowedFilters = [
            ...aiFilters,
            ...(preFilters || []),
            ...(immutableFilters || []),
            ...(activeInitialFilter ? [activeInitialFilter] : [])
        ];
        const allowedSet = new Set(allowedFilters.map((f) => `${f.identifier}:${f.value}`));

        // Remove filters not in the allowed set so they are not duplicated
        currentFilters.forEach((filter) => {
            const key = `${filter.identifier}:${filter.value}`;
            if (!allowedSet.has(key)) {
                api.removeFilter(filter);
            }
        });

        // Create a Set of existing filters to avoid duplicates
        const currentSet = new Set(currentFilters.map((f) => `${f.identifier}:${f.value}`));

        // Add only aiFilters that are not already present in the current filters
        aiFilters.forEach((filter) => {
            const key = `${filter.identifier}:${filter.value}`;
            if (!currentSet.has(key)) {
                api.addFilter(filter);
            }
        });

        prevAIFiltersRef.current = aiFilters;

        api.search();
    }, [activeInitialFilter, aiFilters, api, immutableFilters, preFilters, variables]);

    if (defaultFilters?.length) {
        /**
         * @todo Fix this conditional call to a hook, and
         * remove the eslint-disable comment below.
         */
        // eslint-disable-next-line react-hooks/rules-of-hooks
        useDefaultFilters(id, defaultFilters);
    }

    useEffect(() => {
        if (!isEmpty(preFilters)) {
            preFilters.forEach((preFilter: Filter) => {
                remove(variables.filters, { identifier: preFilter.identifier });
                variables.filters.push(preFilter);
            });
        }
    }, [preFilters]);

    const onEdit = (id) => {
        changeRoute(`${uri}/${id}`);
    };

    const onDelete = (id) => {
        setActiveEntity(id);
        setDeleteConfirmationOpen(true);
    };

    const onEditorClose = () => {
        setEditorOpen(false);
        setActiveEntity('');
        changeRoute(uri);
    };

    const onImportPanelClose = () => {
        setImportPanelOpen(false);
    };

    const Facets = useMemo(() => FacetsList([]), []);

    useDeepCompareEffect(() => {
        if (!isEqual(variables.filters, prevFilters.current)) {
            if (!isSavedFilterChangeRef.current) {
                resetPagination();
            }
            prevFilters.current = variables.filters;
            isSavedFilterChangeRef.current = false;
        }
    }, [variables.filters]);

    useDeepCompareEffect(() => {
        // Avoid duplicate fetches when useQuery has already executed for the same variables.
        doRefetch(true);
    }, [preFilters, sortModel, paginationModel, immutableFilters, skipQuery]);

    useEffect(() => {
        const selectedViewIndex = views.findIndex((view) => view.label === currentView);
        const defaultViewIndex = defaultView && !currentView && views.findIndex((view) => view.label === defaultView);

        if (defaultViewIndex) {
            changeSearch({ [`${id}_${ROUTE_CONSTANTS.view}`]: defaultView });
        } else if (selectedViewIndex === -1) {
            changeSearch({ [`${id}_${ROUTE_CONSTANTS.view}`]: activeView?.label });
        } else {
            setSelectedView(selectedViewIndex);
        }
    }, [views, changeSearch, currentView, id]);

    useEffect(() => {
        if (!loading && error) {
            console.error('Apollo error:', error);
        }
    }, [loading, error]);

    useEffect(() => {
        if (entityId) {
            setActiveEntity(entityId);
            setEditorOpen(true);
        }
    }, [entityId]);

    useEffect(() => {
        setPaginationModel({
            page: pageNumber,
            pageSize
        });
    }, [pageNumber, pageSize]);

    const handleFetchReportData = async (overrides) => {
        const reportVariables = {
            ...variables,
            page: { from: 0, size: entityReportAdapter?.defaultReportPageSize ?? 200 },
            ...overrides
        };

        await fetchReportData(reportVariables);
    };

    const handleConfigurationState = (selectConfiguration: boolean) => {
        setIsConfigurationClicked(selectConfiguration);
    };

    useEffect(() => {
        if (additionalOptimisticData) {
            const newItems = Array.isArray(additionalOptimisticData)
                ? additionalOptimisticData
                : [additionalOptimisticData];

            setCurrentData((prevItems) => {
                if (!prevItems?.results?.hits) return prevItems;
                return {
                    ...prevItems,
                    results: {
                        ...prevItems.results,
                        hits: {
                            ...prevItems.results.hits,
                            items: [...newItems, ...(prevItems.results.hits.items ?? [])]
                        }
                    }
                };
            });
        } else {
            setCurrentData((prev) => {
                const items = prev?.results?.hits?.items;
                if (!items || items.length === 0) return prev;

                const newItems = [...items];
                const idx = newItems.findIndex((it: any) => {
                    const id = it?.id;
                    return typeof id === 'string' && id.startsWith('create');
                });
                if (idx === -1) return prev;

                newItems.splice(idx, 1);

                return {
                    ...prev,
                    results: {
                        ...prev.results,
                        hits: {
                            ...prev.results.hits,
                            items: newItems
                        }
                    }
                };
            });
        }
    }, [additionalOptimisticData]);

    const handleSearch = useCallback(
        (query: string) => {
            setIsSearching(true);
            variables.query = query;
            setPageNumber(0);
            doRefetch();
        },
        [doRefetch, setPageNumber, setIsSearching, variables]
    );

    const isInitialFilterClearDisabled = useMemo(() => {
        const hasCurrentDataLoaded = currentData !== null && currentData !== undefined;
        return !hasCurrentDataLoaded || loading || listLoading;
    }, [currentData, loading, listLoading]);

    const knownFacetsRef = useKnownFacets(currentData?.results?.facets);

    const onCreateStarted = useCallback(() => {
        startCreateSession({
            listPathname: window.location.pathname,
            filterValues: collectViewContextFilterValues(
                (identifier) => api?.getFiltersByIdentifier?.(identifier) || [],
                knownFacetsRef.current
            )
        });
    }, [api, knownFacetsRef, startCreateSession]);

    const renderFacetsPanel = () => {
        if (activeInitialFilter) {
            return (
                <ListViewInitialFilterAlert
                    onClear={handleClearInitialFilter}
                    clearDisabled={isInitialFilterClearDisabled}
                />
            );
        }

        return (
            <FacetsPanel
                id={id}
                Facets={Facets}
                data={currentData}
                loading={loading}
                preferenceEntityKey={preferenceEntityKey}
                promote={promoteFacets}
                facetsVisibility={facetsVisibility}
                searching={enableSearchLoader && isSearching}
                searchPlaceholder={searchPlaceholder}
                onSearch={enableSearch ? handleSearch : undefined}
                quickFilters={quickFilters}
                immutableFilters={immutableFilters || immutableFiltersWithSameIdentifier}
                facetLabelProps={facetLabelProps}
                isSavedFilterChangeRef={isSavedFilterChangeRef}
                enableSaveFilters={!saveFiltersDisabled}
                defaultFilterAutoApply={defaultFilterAutoApply}
                prependViewContent={prependViewContent}
            />
        );
    };

    return (
        <React.Fragment>
            <V className="ListView" sx={{ overflow: 'hidden', p: 1, gap: 1, ...containerSx }}>
                {!saveFiltersDisabled && preferenceEntityKey ? (
                    <SavedFiltersProvider
                        preferenceEntityKey={preferenceEntityKey}
                        immutableFilters={immutableFilters}
                        excludedFilterIdentifiers={resolvedExcludeFiltersFromPersistence}
                        facets={currentData?.results?.facets}
                    >
                        {showBreadCrumbs && (
                            <H gap={1} sx={{ alignItems: 'center' }}>
                                <BreadCrumbs uri={uri} />
                                <SavedFilterControls />
                            </H>
                        )}
                        <PinnedSavedFilterTabs isSavedFilterChangeRef={isSavedFilterChangeRef} />
                    </SavedFiltersProvider>
                ) : (
                    showBreadCrumbs && (
                        <H gap={1} sx={{ alignItems: 'center' }}>
                            <BreadCrumbs uri={uri} />
                        </H>
                    )
                )}
                {showActionsAndFacets && (
                    <H gap={1}>
                        <H sx={{ gap: 1 }}>
                            <ListViewAddActions
                                displayAddActions={displayAddActions}
                                addActions={addActions}
                                createUri={createUri}
                                EntityEditor={EntityEditor}
                                enableImport={enableImport}
                                setImportPanelOpen={setImportPanelOpen}
                                quickAddButtonProps={securedQuickAddButtonProps}
                                uri={uri}
                                setActiveEntity={setActiveEntity}
                                setEditorOpen={setEditorOpen}
                                onCreateStarted={onCreateStarted}
                                mapEntityToCsvRows={mapEntityToCsvRows}
                                tableData={currentData?.results?.hits?.items}
                                exportFileName={exportFileName}
                            />
                            {hasFacets &&
                                (headerTitle ? (
                                    <ListViewHeaderTitle
                                        headerTitle={headerTitle}
                                        FacetsPanelRenderer={renderFacetsPanel}
                                    />
                                ) : (
                                    renderFacetsPanel()
                                ))}
                        </H>
                        {enableShowrunnrAIAgentPanel && (
                            <ShowrunnrAIButton onClick={() => setOpenShowrunnrAIAgentPanel((prev) => !prev)} />
                        )}
                        {showReportButton && (
                            <ReportButton
                                setReportVisible={setReportVisible}
                                views={views}
                                selectedView={selectedView}
                                setSelectedView={setSelectedView}
                                id={id}
                                generateReportButton={resolvedGenerateReportButton}
                                reportButtonLabelToken={resolvedReportButtonLabelToken}
                                onOpenViewPanel={handleOpenReportViewPanel}
                            />
                        )}
                        {showConfigurationButton && (
                            <ListViewConfigurations onClick={() => setIsConfigurationClicked(true)} />
                        )}
                    </H>
                )}
                {activeView && (
                    <H className="ListView_container" sx={{ height: '100%', overflow: 'hidden' }}>
                        {overrideContent ?? (
                            <activeView.Component
                                {...viewProps}
                                id={id}
                                entity={entity}
                                feature={feature}
                                data={currentData}
                                onChangeData={(items: Object, updatedItem?: Object) => {
                                    setCurrentData((prevItems) => ({
                                        ...prevItems,
                                        results: { ...prevItems.results, hits: { ...prevItems.results.hits, items } }
                                    }));
                                    if (updateQuickFilters && currentData?.results) {
                                        updateQuickFilters(currentData.results, updatedItem);
                                    }
                                }}
                                loading={loading || listLoading || props.preferencesLoading}
                                searching={isSearching}
                                paginationModel={paginationModel}
                                refetch={refetch}
                                setListLoading={setListLoading}
                                onEdit={EntityEditor && !editDisabled ? onEdit : undefined}
                                onDelete={props.onDelete ? onDelete : undefined}
                                page={pageNumber}
                                onPageChange={(pageNumber) => setPageNumber(pageNumber)}
                                pageSize={pageSize}
                                onPageSizeChange={(pageSize) => setPageSize(pageSize)}
                                sortModel={sortModelMemoized}
                                onSortModelChange={(newSortModel) => {
                                    if (!isEqual(sortModel, newSortModel)) {
                                        setPageNumber(0);
                                        if (!isEmpty(newSortModel)) {
                                            setSortModel(newSortModel[0]);
                                        } else {
                                            setSortModel({ field: UNSET_SORT, sort: UNSET_SORT });
                                        }
                                    }
                                }}
                                openShowrunnrAIAgentPanel={openShowrunnrAIAgentPanel}
                                setOpenShowrunnrAIAgentPanel={setOpenShowrunnrAIAgentPanel}
                                uri={uri}
                                slots={props.slots}
                                setImportPanelOpen={setImportPanelOpen}
                                controlledSidePanelId={resolvedControlledSidePanelId}
                                onControlledSidePanelClose={handleControlledSidePanelClose}
                                isConfigurationClicked={isConfigurationClicked}
                                handleConfigurationState={handleConfigurationState}
                                setCustomVariables={props.setCustomVariables}
                                setAiData={onChangeAIQuery}
                                externalAddTriggerValue={props.externalAddTriggerValue}
                                onExternalAddDisabledChange={props.onExternalAddDisabledChange}
                                reportSidePanelContextTerm={reportContextTerm}
                            />
                        )}
                    </H>
                )}
            </V>
            {ImportView && (
                <ImportView onSave={doRefetch} importPanelOpen={importPanelOpen} onClose={onImportPanelClose} />
            )}
            {resolvedGenerateReportButton && reportVisible && entityReportAdapter && (
                <GenerateReportStepper
                    setReportVisible={setReportVisible}
                    reportVisible={reportVisible}
                    latestSearchResults={reportData?.results?.hits?.items ?? []}
                    sortOrders={reportData?.results?.sortOrders}
                    onFetchReportData={handleFetchReportData}
                    reportLoading={reportLoading}
                    facets={currentData?.results?.facets}
                    entityReportAdapter={entityReportAdapter}
                    preferenceEntityKey={preferenceEntityKey}
                />
            )}
            {EntityEditor && editorOpen && (
                <EntityEditor id={activeEntity} editorOpen={editorOpen} onClose={onEditorClose} onSave={doRefetch} />
            )}
            {detailsVisible && EntityDetails ? (
                <EntityDetails
                    id={activeEntity}
                    onSave={doRefetch}
                    onClose={(redirect) => {
                        setDetailsVisible(false);
                        if (redirect) {
                            changeRoute(redirect);
                        }
                    }}
                />
            ) : (
                <React.Fragment />
            )}
            <Conditional condition={props.onDelete}>
                <Confirm
                    open={deleteConfirmationOpen}
                    title={<I18n token={'delete.confirm.title'} />}
                    okLabelI18n="delete.confirm.title"
                    body={
                        <Typography>
                            <I18n token={'delete.confirm.listView.message'} />
                        </Typography>
                    }
                    onConfirm={() => {
                        setDeleteConfirmationOpen(false);
                        Promise.resolve(props.onDelete(activeEntity)).then(() => {
                            setTimeout(() => {
                                doRefetch();
                            }, 500);
                        });
                    }}
                    onCancel={() => {
                        setDeleteConfirmationOpen(false);
                    }}
                />
            </Conditional>
        </React.Fragment>
    );
}
